import { LogLevel } from './logger';

export interface LoggerConfig {
  enabled: boolean;
  globalLevel: LogLevel;
  componentLevels: Record<string, LogLevel>;
  disabledComponents: string[];
  prettyPrint: boolean;
}

const DEFAULT_CONFIG: LoggerConfig = {
  enabled: true,
  globalLevel: LogLevel.ERROR, // Only show errors by default (super_admin can enable debug via settings)
  componentLevels: {},
  disabledComponents: [],
  prettyPrint: true,
};

const STORAGE_KEY = 'panther_logger_config';

class LoggerConfigManager {
  private static instance: LoggerConfigManager;
  private config: LoggerConfig;

  private constructor() {
    this.config = this.loadConfig();
    this.exposeToWindow();
  }

  public static getInstance(): LoggerConfigManager {
    if (!LoggerConfigManager.instance) {
      LoggerConfigManager.instance = new LoggerConfigManager();
    }
    return LoggerConfigManager.instance;
  }

  private loadConfig(): LoggerConfig {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_CONFIG, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load logger config from localStorage:', error);
    }
    return { ...DEFAULT_CONFIG };
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
      (window as any).__PANTHER_LOG_CONFIG__ = this.config;
    } catch (error) {
      console.error('Failed to save logger config to localStorage:', error);
    }
  }

  public getConfig(): LoggerConfig {
    return { ...this.config };
  }

  public enable(): void {
    this.config.enabled = true;
    this.saveConfig();
    console.log('%c[Logger] Logging enabled', 'color: #10B981; font-weight: bold');
  }

  public disable(): void {
    this.config.enabled = false;
    this.saveConfig();
    console.log('%c[Logger] Logging disabled', 'color: #EF4444; font-weight: bold');
  }

  public setLevel(level: LogLevel | keyof typeof LogLevel): void {
    const levelValue = typeof level === 'string' ? LogLevel[level] : level;
    this.config.globalLevel = levelValue;
    this.saveConfig();
    console.log(
      `%c[Logger] Global level set to: ${LogLevel[levelValue]}`,
      'color: #3B82F6; font-weight: bold'
    );
  }

  public setComponentLevel(component: string, level: LogLevel | keyof typeof LogLevel): void {
    const levelValue = typeof level === 'string' ? LogLevel[level] : level;
    this.config.componentLevels[component] = levelValue;
    this.saveConfig();
    console.log(
      `%c[Logger] Component "${component}" level set to: ${LogLevel[levelValue]}`,
      'color: #3B82F6; font-weight: bold'
    );
  }

  public enableComponent(component: string): void {
    this.config.disabledComponents = this.config.disabledComponents.filter(
      (c) => c !== component
    );
    this.saveConfig();
    console.log(
      `%c[Logger] Component "${component}" enabled`,
      'color: #10B981; font-weight: bold'
    );
  }

  public disableComponent(component: string): void {
    if (!this.config.disabledComponents.includes(component)) {
      this.config.disabledComponents.push(component);
    }
    this.saveConfig();
    console.log(
      `%c[Logger] Component "${component}" disabled`,
      'color: #EF4444; font-weight: bold'
    );
  }

  public setPrettyPrint(enabled: boolean): void {
    this.config.prettyPrint = enabled;
    this.saveConfig();
    console.log(
      `%c[Logger] Pretty print ${enabled ? 'enabled' : 'disabled'}`,
      'color: #3B82F6; font-weight: bold'
    );
  }

  public reset(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.saveConfig();
    console.log('%c[Logger] Configuration reset to defaults', 'color: #F59E0B; font-weight: bold');
  }

  public listComponents(): string[] {
    return Array.from(
      new Set([
        ...Object.keys(this.config.componentLevels),
        ...this.config.disabledComponents,
      ])
    ).sort();
  }

  private exposeToWindow(): void {
    (window as any).__PANTHER_LOG_CONFIG__ = this.config;
  }
}

export const loggerConfig = LoggerConfigManager.getInstance();
export default loggerConfig;
